import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trees, LogOut, Upload, Image as ImageIcon, Check, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [trees, setTrees] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCoverPhoto, setSelectedCoverPhoto] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  
  // Homepage box content state
  const [box1Title, setBox1Title] = useState('Multiple Audio Tracks');
  const [box1Text, setBox1Text] = useState('Listen to indigenous, scientific, logger, horticultural, and poetic perspectives—all telling unique stories about each tree.');
  const [box2Title, setBox2Title] = useState('Interactive Map');
  const [box2Text, setBox2Text] = useState('Explore the geographic distribution of Australian trees with precise geo-location data on an interactive map.');
  const [box3Title, setBox3Title] = useState('Rich Information');
  const [box3Text, setBox3Text] = useState('Detailed scientific data, habitat information, conservation status, and cultural significance for each tree species.');
  
  // Hero text state
  const [heroTitle, setHeroTitle] = useState('TreeMuseum');
  const [heroSubtitle, setHeroSubtitle] = useState('Experience Australian trees through multiple perspectives—indigenous wisdom, scientific knowledge, horticultural expertise, logging history, and poetic beauty.');
  const [button1Text, setButton1Text] = useState('Explore Map');
  const [button2Text, setButton2Text] = useState('View Gallery');
  
  // Features section state
  const [featuresTitle, setFeaturesTitle] = useState('Discover Trees Through Sound & Story');
  const [featuresSubtitle, setFeaturesSubtitle] = useState('Every tree tells multiple stories. Listen to diverse voices and perspectives.');
  
  // Admin credentials state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingCredentials, setUpdatingCredentials] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [treesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/trees`),
        axios.get(`${API}/settings`)
      ]);
      setTrees(treesRes.data);
      setSettings(settingsRes.data);
      setSelectedCoverPhoto(settingsRes.data.cover_photo_url);
      
      // Load homepage box content
      if (settingsRes.data.homepage_box1_title) setBox1Title(settingsRes.data.homepage_box1_title);
      if (settingsRes.data.homepage_box1_text) setBox1Text(settingsRes.data.homepage_box1_text);
      if (settingsRes.data.homepage_box2_title) setBox2Title(settingsRes.data.homepage_box2_title);
      if (settingsRes.data.homepage_box2_text) setBox2Text(settingsRes.data.homepage_box2_text);
      if (settingsRes.data.homepage_box3_title) setBox3Title(settingsRes.data.homepage_box3_title);
      if (settingsRes.data.homepage_box3_text) setBox3Text(settingsRes.data.homepage_box3_text);
      
      // Load hero text
      if (settingsRes.data.homepage_hero_title) setHeroTitle(settingsRes.data.homepage_hero_title);
      if (settingsRes.data.homepage_hero_subtitle) setHeroSubtitle(settingsRes.data.homepage_hero_subtitle);
      if (settingsRes.data.homepage_button1_text) setButton1Text(settingsRes.data.homepage_button1_text);
      if (settingsRes.data.homepage_button2_text) setButton2Text(settingsRes.data.homepage_button2_text);
      
      // Load features section text
      if (settingsRes.data.homepage_features_title) setFeaturesTitle(settingsRes.data.homepage_features_title);
      if (settingsRes.data.homepage_features_subtitle) setFeaturesSubtitle(settingsRes.data.homepage_features_subtitle);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const handleCoverPhotoSelect = (tree) => {
    setSelectedCoverPhoto(tree.image_url);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Logo file must be less than 5MB');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleUpdateCredentials = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      toast.error('Current password is required');
      return;
    }
    
    if (newPassword && newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (!newUsername && !newPassword) {
      toast.error('Please provide a new username or password');
      return;
    }

    setUpdatingCredentials(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.put(
        `${API}/admin/credentials`,
        {
          current_password: currentPassword,
          new_username: newUsername || undefined,
          new_password: newPassword || undefined,
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Admin credentials updated successfully!');
      
      // Clear form
      setCurrentPassword('');
      setNewUsername('');
      setNewPassword('');
      setConfirmPassword('');
      
      // If username changed, need to re-login
      if (newUsername) {
        toast.info('Username changed. Please log in again with new credentials.');
        setTimeout(() => {
          localStorage.removeItem('admin_token');
          navigate('/admin/login');
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating credentials:', error);
      toast.error(error.response?.data?.detail || 'Failed to update credentials');
    } finally {
      setUpdatingCredentials(false);
    }
  };
  const handleSaveSettings = async () => {
    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Upload logo if new file selected
      let logoUrl = settings?.logo_url;
      let logoS3Key = settings?.logo_s3_key;
      
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        
        const uploadRes = await axios.post(`${API}/upload/logo`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        
        logoUrl = uploadRes.data.url;
        logoS3Key = uploadRes.data.s3_key;
      }

      // Find the tree that matches the selected cover photo
      const coverTree = trees.find(t => t.image_url === selectedCoverPhoto);
      
      // Update settings
      await axios.put(
        `${API}/settings`,
        {
          cover_photo_url: selectedCoverPhoto,
          cover_photo_s3_key: coverTree?.s3_image_key || null,
          logo_url: logoUrl,
          logo_s3_key: logoS3Key,
          homepage_box1_title: box1Title,
          homepage_box1_text: box1Text,
          homepage_box2_title: box2Title,
          homepage_box2_text: box2Text,
          homepage_box3_title: box3Title,
          homepage_box3_text: box3Text,
          homepage_hero_title: heroTitle,
          homepage_hero_subtitle: heroSubtitle,
          homepage_button1_text: button1Text,
          homepage_button2_text: button2Text,
          homepage_features_title: featuresTitle,
          homepage_features_subtitle: featuresSubtitle,
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success('Settings saved successfully!');
      fetchData();
      setLogoFile(null);
      setLogoPreview(null);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="settings-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary" data-testid="admin-settings-page">
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Trees className="w-6 h-6 text-primary" />
              <span className="text-xl font-heading font-bold text-primary">TreeMuseum Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/admin/dashboard"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                data-testid="dashboard-link"
              >
                Dashboard
              </Link>
              <Link
                to="/"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                data-testid="view-site-link"
              >
                View Site
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-destructive transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary mb-2" data-testid="settings-title">
            App Settings
          </h1>
          <p className="text-sm text-muted-foreground">
            Customize your TreeMuseum appearance
          </p>
        </div>

        <div className="space-y-8">
          {/* Logo Section */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-xl font-heading font-bold text-primary mb-4">App Logo</h2>
            
            <div className="flex items-start gap-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground mb-3">
                  Upload New Logo (Max 5MB)
                </label>
                <label className="cursor-pointer">
                  <div className="flex items-center justify-center gap-2 px-6 py-4 border-2 border-dashed border-input rounded-lg hover:border-primary transition-colors">
                    <Upload className="w-5 h-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {logoFile ? logoFile.name : 'Click to upload logo (PNG, JPEG, SVG)'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                    onChange={handleLogoChange}
                    className="hidden"
                    data-testid="logo-upload-input"
                  />
                </label>
              </div>

              {(logoPreview || settings?.logo_url) && (
                <div className="w-48">
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Logo Preview
                  </label>
                  <div className="p-4 border border-border rounded-lg bg-secondary/30">
                    <img
                      src={logoPreview || settings?.logo_url}
                      alt="Logo"
                      className="max-h-20 mx-auto"
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Homepage Hero Section */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-xl font-heading font-bold text-primary mb-4">Homepage Hero Section</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Edit the main hero section text and buttons on the homepage
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Hero Title</label>
                <input
                  type="text"
                  value={heroTitle}
                  onChange={(e) => setHeroTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="TreeMuseum"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Hero Subtitle</label>
                <textarea
                  value={heroSubtitle}
                  onChange={(e) => setHeroSubtitle(e.target.value)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  rows="3"
                  placeholder="Experience Australian trees..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Button 1 Text</label>
                  <input
                    type="text"
                    value={button1Text}
                    onChange={(e) => setButton1Text(e.target.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Explore Map"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Button 2 Text</label>
                  <input
                    type="text"
                    value={button2Text}
                    onChange={(e) => setButton2Text(e.target.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="View Gallery"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Homepage Features Section */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-xl font-heading font-bold text-primary mb-4">Homepage Features Section</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Edit the features section heading and subtitle
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Features Title</label>
                <input
                  type="text"
                  value={featuresTitle}
                  onChange={(e) => setFeaturesTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Discover Trees Through Sound & Story"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Features Subtitle</label>
                <textarea
                  value={featuresSubtitle}
                  onChange={(e) => setFeaturesSubtitle(e.target.value)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  rows="2"
                  placeholder="Every tree tells multiple stories..."
                />
              </div>
            </div>
          </div>

          {/* Homepage Content Boxes Section */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-xl font-heading font-bold text-primary mb-4">Homepage Content Boxes</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Edit the text for the three feature boxes on the homepage
            </p>

            <div className="space-y-6">
              {/* Box 1 */}
              <div className="p-4 border border-border rounded-lg bg-secondary/20">
                <h3 className="text-sm font-semibold text-primary mb-3">Box 1 (Multiple Audio Tracks)</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Title</label>
                    <input
                      type="text"
                      value={box1Title}
                      onChange={(e) => setBox1Title(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      placeholder="Box 1 Title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Description</label>
                    <textarea
                      value={box1Text}
                      onChange={(e) => setBox1Text(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      rows="3"
                      placeholder="Box 1 Description"
                    />
                  </div>
                </div>
              </div>

              {/* Box 2 */}
              <div className="p-4 border border-border rounded-lg bg-secondary/20">
                <h3 className="text-sm font-semibold text-primary mb-3">Box 2 (Interactive Map)</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Title</label>
                    <input
                      type="text"
                      value={box2Title}
                      onChange={(e) => setBox2Title(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      placeholder="Box 2 Title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Description</label>
                    <textarea
                      value={box2Text}
                      onChange={(e) => setBox2Text(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      rows="3"
                      placeholder="Box 2 Description"
                    />
                  </div>
                </div>
              </div>

              {/* Box 3 */}
              <div className="p-4 border border-border rounded-lg bg-secondary/20">
                <h3 className="text-sm font-semibold text-primary mb-3">Box 3 (Rich Information)</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Title</label>
                    <input
                      type="text"
                      value={box3Title}
                      onChange={(e) => setBox3Title(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      placeholder="Box 3 Title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">Description</label>
                    <textarea
                      value={box3Text}
                      onChange={(e) => setBox3Text(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      rows="3"
                      placeholder="Box 3 Description"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cover Photo Section */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-xl font-heading font-bold text-primary mb-4">Homepage Cover Photo</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Select an image from your tree library to use as the homepage hero background
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {trees.map((tree) => (
                <div
                  key={tree.id}
                  onClick={() => handleCoverPhotoSelect(tree)}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:border-primary ${
                    selectedCoverPhoto === tree.image_url
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-border'
                  }`}
                  data-testid={`cover-option-${tree.id}`}
                >
                  <img
                    src={tree.image_url}
                    alt={tree.name}
                    className="w-full h-32 object-cover"
                  />
                  {selectedCoverPhoto === tree.image_url && (
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <Check className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                    <p className="text-xs text-white font-medium truncate">{tree.name}</p>
                  </div>
                </div>
              ))}
            </div>

            {trees.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No tree images available. Add trees first.</p>
              </div>
            )}
          </div>

          {/* Admin Credentials Section */}
          <div className="bg-white border border-border rounded-lg p-6">
            <h2 className="text-xl font-heading font-bold text-primary mb-4">Admin Credentials</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Update your admin username and/or password
            </p>

            <form onSubmit={handleUpdateCredentials} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Current Password <span className="text-destructive">*</span>
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter current password"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    New Username (optional)
                  </label>
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Leave blank to keep current"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    New Password (optional)
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Leave blank to keep current"
                  />
                </div>
              </div>

              {newPassword && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Confirm New Password <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Re-enter new password"
                    required
                  />
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={updatingCredentials}
                  className="flex items-center gap-2 px-6 py-2 bg-accent text-foreground rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatingCredentials ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Update Credentials
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={uploading}
              className="flex items-center gap-2 px-8 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50 shadow-md"
              data-testid="save-settings-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSettings;
